import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faSmile, 
  faHashtag, 
  faAt, 
  faImages, 
  faFilm, 
  faClipboardList,
} from "@fortawesome/free-solid-svg-icons";
import _ from "lodash";
import { useSocialPostCore } from "../../../../cores/socialPost";
import { getSocialPostRules, PickerMode } from "../../../../cores/socialPost/config";
import { useGlobalCore } from "../../../../cores/globals";
import {
  ADD_SOCIAL_IMAGE_MODAL,
  ADD_SOCIAL_VIDEO_MODAL,
  EMOJI_FORM,
  OPENAI_SOCIAL_FORM,
  TEMPLATES_SOCIAL_FORM,
} from "../../../../constants/modal.constant";
import { SocialType } from "../../../../cores/socialPost/interfaces/postError";
import { OpenAiIcon } from "../../social/OpenAi/OpenAiIcon";
import { IconMapper } from "../selector/SocialPostSelector";
import "./ModernTextEditor.scss";

interface Props {
  onClickEmoji?: () => void;
  onClickHashtag?: () => void;
  onClickMention?: () => void;
  onClickImages?: () => void;
  onClickVideo?: () => void;
  onClickTemplate?: () => void;
  onClickOpenAi?: () => void;
  activePicker: PickerMode;
  socialType: SocialType;
}

export const ModernTextEditor = (props: Props) => {
  const intl = useIntl();
  const {
    onClickEmoji,
    onClickHashtag,
    onClickMention,
    onClickImages,
    onClickVideo,
    onClickTemplate,
    onClickOpenAi,
    activePicker,
    socialType,
  } = props;
  const {
    text,
    addText,
    images,
    video,
    socialProviders,
    selectedPost,
    inputCursorIndex,
    addInputCursorIndex,
    updateMentions,
  } = useSocialPostCore();
  const { isModal } = useGlobalCore();

  const isProviderSelected = useMemo(
    () => socialProviders.length > 0,
    [socialProviders.length]
  );


  const handleAddText = useCallback(
    (txt: string) => {
      const offset = txt.length - text.length;
      addText(txt);
      updateMentions(offset, inputCursorIndex);
    },
    [inputCursorIndex, text, addText, updateMentions]
  );

  const getCharacterCount = () => {
    return text.length;
  };

  const getHashtagCount = () => {
    // Contar hashtags en el texto
    const hashtagMatches = text.match(/#\w+/g);
    return hashtagMatches ? hashtagMatches.length : 0;
  };

  const getMaxCharacters = useMemo(() => {
    if (socialProviders.length === 0) return 2200; // Valor por defecto
    
    // Obtener el límite más restrictivo de las redes seleccionadas
    const limits = socialProviders.map(provider => {
      switch (provider.id.toLowerCase()) {
        case 'instagram':
          return 2200;
        case 'facebook':
          return 63206;
        case 'twitter':
          return 280;
        case 'linkedin':
        case 'linkedinbusiness':
          return 1300;
        case 'tiktok':
          return 4000;
        default:
          return 2200;
      }
    });
    
    return Math.min(...limits);
  }, [socialProviders]);

  const getMaxHashtags = useMemo(() => {
    if (socialProviders.length === 0) return 20; // Valor por defecto
    
    // Obtener el límite más restrictivo de hashtags
    const limits = socialProviders.map(provider => {
      switch (provider.id.toLowerCase()) {
        case 'instagram':
          return 20;
        case 'facebook':
          return 30;
        case 'twitter':
          return 10;
        case 'linkedin':
        case 'linkedinbusiness':
          return 10;
        case 'tiktok':
          return 30;
        default:
          return 20;
      }
    });
    
    return Math.min(...limits);
  }, [socialProviders]);

  const isCharacterLimitExceeded = useMemo(() => {
    return text.length > getMaxCharacters;
  }, [text.length, getMaxCharacters]);

  const isHashtagLimitExceeded = useMemo(() => {
    return getHashtagCount() > getMaxHashtags;
  }, [text, getMaxHashtags]);

  /** TikTok (y futuras redes solo-video): ocultar adjuntar imágenes si todas las redes seleccionadas lo prohíben */
  const imagesToolbarBlocked = useMemo(() => {
    if (socialProviders.length === 0) return false;
    return socialProviders.every((sp) => {
      const rules = getSocialPostRules(String(sp.id));
      return rules?.imageAllowed === false;
    });
  }, [socialProviders]);

  return (
    <div className="modern-text-editor">
      {/* Header with character count */}
     

      {/* Main text area with integrated counters */}
      <div className="text-area-container">
        <div className="textarea-wrapper">
          <textarea
            className="modern-textarea"
            value={text}
            rows={6}
            placeholder={intl.formatMessage({ id: "app.socialMediaPost.addText" })}
            disabled={!isProviderSelected}
            style={{ resize: isProviderSelected ? "vertical" : "none" }}
            onChange={(e) => handleAddText(e.target.value)}
            onKeyUp={(ev: any) => addInputCursorIndex(ev.target.selectionStart)}
            onClick={(ev: any) => addInputCursorIndex(ev.target.selectionStart)}
          />
          
          {/* Character count info inside textarea */}
          <div className="text-info-inside">
            <div className="counters-container">
              <span className={`hashtag-count ${isHashtagLimitExceeded ? 'exceeded' : ''}`}>
                {getHashtagCount()} / {getMaxHashtags} #
              </span>
              <span className={`character-count ${isCharacterLimitExceeded ? 'exceeded' : ''}`}>
                {getCharacterCount()} / {getMaxCharacters}
              </span>
              {socialProviders.length === 1 && (
                <span className="network-indicator">
                  {_.isFunction(IconMapper[socialProviders[0].id.toLowerCase()]) &&
                    IconMapper[socialProviders[0].id.toLowerCase()]({ active: true, size: "1x" })}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="editor-toolbar">
        <div className="toolbar-left">
          {onClickEmoji && (
            <button
              className={`toolbar-button ${isModal(EMOJI_FORM) ? 'active' : ''}`}
              onClick={onClickEmoji}
              title="Emojis"
            >
              <FontAwesomeIcon icon={faSmile} />
            </button>
          )}
          {onClickHashtag && (
            <button
              className={`toolbar-button ${activePicker === PickerMode.HashTag ? 'active' : ''}`}
              onClick={onClickHashtag}
              title="Hashtags"
            >
              <FontAwesomeIcon icon={faHashtag} />
            </button>
          )}
          {onClickMention && (
            <button
              className={`toolbar-button ${activePicker === PickerMode.Mention ? 'active' : ''}`}
              onClick={onClickMention}
              title="Menciones"
            >
              <FontAwesomeIcon icon={faAt} />
            </button>
          )}
          {onClickTemplate && (
            <button
              className={`toolbar-button ${isModal(TEMPLATES_SOCIAL_FORM) ? 'active' : ''}`}
              onClick={onClickTemplate}
              title="Plantillas"
            >
              <FontAwesomeIcon icon={faClipboardList} />
            </button>
          )}
          {onClickOpenAi && (
            <button
              className={`toolbar-button ${isModal(OPENAI_SOCIAL_FORM) ? 'active' : ''}`}
              onClick={onClickOpenAi}
              title="OpenAI"
            >
              <OpenAiIcon
                size="1x"
                active={isModal(OPENAI_SOCIAL_FORM)}
                className="openai-icon"
              />
            </button>
          )}
        </div>
        
        <div className="toolbar-right">
          {onClickImages && (
            <button
              type="button"
              className={`toolbar-button ${isModal(ADD_SOCIAL_IMAGE_MODAL) || images.length || (selectedPost?.post.type === "image" && selectedPost?.media.length) ? 'active' : ''}`}
              onClick={onClickImages}
              disabled={imagesToolbarBlocked}
              title={
                imagesToolbarBlocked
                  ? intl.formatMessage({
                      id: "app.socialMediaPost.toolbarImagesDisabledVideoOnly",
                    })
                  : "Imágenes"
              }
            >
              <FontAwesomeIcon icon={faImages} />
            </button>
          )}
          {onClickVideo && (
            <button
              className={`toolbar-button ${isModal(ADD_SOCIAL_VIDEO_MODAL) || video || (selectedPost?.post.type === "video" && selectedPost?.media.length) ? 'active' : ''}`}
              onClick={onClickVideo}
              title="Videos"
            >
              <FontAwesomeIcon icon={faFilm} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
